/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { VehicleDamagedIndicator, SkeletonUI, Loader } from '..';
import { constants as damagesConstants } from '../../redux/damages';
import { constants as workflowsConstants } from '../../redux/workflows';
import {
  constants,
  actions as windshieldRepairabilityProfileActions,
  selectors as windshieldRepairabilityProfileSelectors
} from '../../redux/windshieldRepairabilityProfiles';
import { ReactComponent as IconCaretRight } from '../../assets/img/icn-caret-right.svg';
import { ReactComponent as IconCaretDown } from '../../assets/img/icn-caret-down.svg';
import { ReactComponent as IconHelp } from '../../assets/img/icn-help.svg';
import { Tooltip } from '../Form';
import { makeGet, makeGetFile } from '../../api/ajax';
import { useLoadExperimentalFeatures } from '../../redux/experimentalFeatures';
import { constants as sessionConstants } from '../../redux/session';
import { actions as photoSeriesActions, constants as photoSeriesConstants } from '../../redux/photoSeries';
import { authenticationService } from '../../services';
import photoSeriesHelpers from '../../redux/photoSeries/helpers';
import { formatDateTime } from '../../utils/date';
import ProductInfoModal from './ProductInfoModal/ProductInfoModal';
import { ReactComponent as IconYellowWarning } from '../../assets/img/icn-warning-yellow.svg';

import './sass/photo-series-damages.scss';

const { VIEW_PRODUCT_INFO, EDIT_PRODUCT_INFO } = sessionConstants.PERMISSION_TYPES;

const ProductInformation = ({ productInfo, productInfoStatus, bodyPart, onEditClick }) => {
  const { hasPermission } = authenticationService;

  // Currently show this only for windshield
  if (bodyPart !== damagesConstants.BODY_PART.WINDSHIELD) {
    return <span>-</span>;
  }

  let displayValue = productInfo?.ArgicEuroCode ?? productInfo?.OeCode ?? '-';
  const isAnalyzing = productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.ANALYZING;

  const showErrorInfoStatus = productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.NOT_FOUND
    || productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.INVALID_VIN;

  if (showErrorInfoStatus || isAnalyzing) {
    displayValue = photoSeriesConstants.PRODUCT_INFO_STATUS_DESCRIPTION[productInfoStatus];
  }

  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex align-items-center w-100">
        { showErrorInfoStatus && <IconYellowWarning className="svg-md mr-1" />}
        {
          isAnalyzing && (
            <div className="position-relative mr-1" style={{ width: '1rem', height: '1rem' }}><Loader size="tiny" isRelative noDelay /></div>
          )
        }
        <span>{displayValue}</span>
      </div>
      {hasPermission(EDIT_PRODUCT_INFO) && !isAnalyzing && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={(e) => {
            e.stopPropagation();
            onEditClick();
          }}
          style={{ fontSize: '0.875rem', textAlign: 'left' }}
        >
          Edit
        </button>
      )}
    </div>
  );
};

ProductInformation.propTypes = {
  productInfo: PropTypes.shape({
    OeCode: PropTypes.string,
    ArgicEuroCode: PropTypes.string,
    CalibrationNeeded: PropTypes.string
  }),
  productInfoStatus: PropTypes.number,
  bodyPart: PropTypes.number.isRequired,
  onEditClick: PropTypes.func.isRequired
};

const ChipReplaceSizeWarning = ({ damage, windshieldRepairProfileId }) => {
  const dispatch = useDispatch();
  const repairProfile = useSelector((state) => windshieldRepairabilityProfileSelectors
    .selectWindshieldRepairabilityProfileById(state, windshieldRepairProfileId));

  useEffect(() => {
    if (!windshieldRepairProfileId || repairProfile) return undefined;

    const subscription = makeGet(`windshieldRepairProfiles/${windshieldRepairProfileId}`).subscribe(
      ({ response }) => dispatch(windshieldRepairabilityProfileActions.loadWindshieldRepairabilityProfileSuccess(response))
    );

    return () => subscription.unsubscribe();
  }, [windshieldRepairProfileId, repairProfile, dispatch]);

  if (!repairProfile?.repairableChipSizeInCm) return null;

  return (
    <Tooltip>
      {`Chip needs replacing, size ${damage.damageSize.toFixed(1)} cm is >Max (${repairProfile.repairableChipSizeInCm} cm)`}
    </Tooltip>
  );
};

ChipReplaceSizeWarning.propTypes = {
  damage: PropTypes.shape({
    damageSize: PropTypes.number
  }).isRequired,
  windshieldRepairProfileId: PropTypes.string
};

ChipReplaceSizeWarning.defaultProps = {
  windshieldRepairProfileId: null
};

const DamageRow = ({ photoSeries, bodyPart, damages, experimentalFeatureType, showExperimentalFeature }) => {
  const [open, setOpen] = useState(false);
  const [showProductInfoModal, setShowProductInfoModal] = useState(false);
  const repairEstimation = photoSeries.repairEstimations.find((re) => re.bodyPart === bodyPart);
  const getRepairDecisionName = (repairDecision) => repairDecision === undefined || repairDecision === null
    ? '-'
    : damagesConstants.REPAIR_DECISION_NAME[repairDecision];

  const getRepairDecisionConfidence = (repairDecisionConfidence) => repairDecisionConfidence === null || repairDecisionConfidence === undefined
    ? '-'
    : `${Math.round(repairDecisionConfidence * 100)}%`;

  const isInDriversFovWarningTooltipMessage = 'Repairable damage was located in the driver\'s FoV.';
  const showIsInDriversFovWarningTooltipInHeader = repairEstimation?.repairDecision === damagesConstants.REPAIR_DECISION.REPAIR
    && damages.some((damage) => damage.isInDriversFovWarning);

  const showConfidence = showExperimentalFeature(experimentalFeatureType.SHOW_WINDSHIELD_REPAIR_CONFIDENCE);
  const { hasPermission } = authenticationService;

  const productInfo = repairEstimation?.productInfo;
  const isPolling = productInfo?.productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.ANALYZING;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isPolling) return;

    let subscription = null;

    const intervalId = setInterval(() => {
      if (subscription) subscription.unsubscribe();
      subscription = makeGet(`photoseries/${photoSeries.id}/productInfo/${bodyPart}`).subscribe(
        ({ response }) => {
          dispatch(photoSeriesActions.updateRepairEstimationProductInfoSuccess(photoSeries.id, bodyPart, response));
        }
      );
    }, 10000);

    return () => {
      clearInterval(intervalId);
      if (subscription) subscription.unsubscribe();
    };
  }, [isPolling, photoSeries.id, bodyPart, dispatch]);

  // Tooltip for windshield with "No action required"
  const isWindshieldNoActionRequired = bodyPart === damagesConstants.BODY_PART.WINDSHIELD
    && repairEstimation?.repairDecision === damagesConstants.REPAIR_DECISION.NO_ACTION_REQUIRED;
  const windshieldNoActionTooltip = 'Our AI could not find damages on this windshield';

  return (
    <>
      <div className="damages-table__row-wrapper">
        <div className="damages-table__row" role="row" onClick={() => setOpen(!open)} tabIndex={0}>
          <div className="damages-table__row-part">
            { damages.length > 0 ? (!open ? <IconCaretRight /> : <IconCaretDown />) : null }
            <span>{damagesConstants.BODY_PART_NAMES[bodyPart]} ({damages.length})</span>
          </div>
          <div
            className={`damages-table__row-repair-decision ${isWindshieldNoActionRequired ? 'tooltip-on-hover' : ''}`}
            data-tooltip={isWindshieldNoActionRequired ? windshieldNoActionTooltip : undefined}
          >
            {getRepairDecisionName(repairEstimation?.repairDecision)}
            {showIsInDriversFovWarningTooltipInHeader && <Tooltip>{isInDriversFovWarningTooltipMessage}</Tooltip>}
            {isWindshieldNoActionRequired && (
              <div className="d-inline help">
                <IconHelp />
              </div>
            )}
          </div>
          {
            showConfidence && (
              <div className="damages-table__row-repair-decision-confidence">
                {getRepairDecisionConfidence(repairEstimation?.confidence)}
              </div>
            )
          }
          {
            hasPermission(VIEW_PRODUCT_INFO) && (
              <div className="damages-table__row-product-id">
                <ProductInformation
                  productInfo={productInfo?.productInfo}
                  productInfoStatus={productInfo?.productInfoStatus}
                  bodyPart={bodyPart}
                  onEditClick={() => setShowProductInfoModal(true)}
                />
              </div>
            )
          }
        </div>
        {
          open && damages.map((damage, index) => {
            // Tooltip for windshield damages with "No action required"
            const isDamageWindshieldNoAction = bodyPart === damagesConstants.BODY_PART.WINDSHIELD
              && damage.repairDecision === damagesConstants.REPAIR_DECISION.NO_ACTION_REQUIRED;
            const damageNoActionTooltip = 'Our AI could not verify this damage';

            const isChipReplaceWithSize = damage.damageType === damagesConstants.DAMAGE_TYPE.CHIP
              && damage.repairDecision === damagesConstants.REPAIR_DECISION.REPLACE
              && !!damage.damageSize;

            return (
              <div className="damages-table__row-sub-row" key={damage.damageId}>
                <span>{index + 1}. {damagesConstants.getDamageTypeName(damage)}</span>
                <span
                  className={isDamageWindshieldNoAction ? 'tooltip-on-hover' : ''}
                  data-tooltip={isDamageWindshieldNoAction ? damageNoActionTooltip : undefined}
                >
                  {getRepairDecisionName(damage.repairDecision)}
                  {damage.isInDriversFovWarning && <Tooltip>{isInDriversFovWarningTooltipMessage}</Tooltip>}
                  {isDamageWindshieldNoAction && (
                    <div className="d-inline help">
                      <IconHelp />
                    </div>
                  )}
                  {isChipReplaceWithSize && (
                    <ChipReplaceSizeWarning
                      damage={damage}
                      windshieldRepairProfileId={photoSeries.workflow?.windshieldRepairProfileId}
                    />
                  )}
                </span>
                { showConfidence && (<span>{getRepairDecisionConfidence(damage.repairDecisionConfidence)}</span>)}
                { hasPermission(VIEW_PRODUCT_INFO) && <span /> }
              </div>
            );
          })
        }
      </div>
      {showProductInfoModal && (
        <ProductInfoModal
          show={showProductInfoModal}
          onModalClose={() => setShowProductInfoModal(false)}
          photoSeriesId={photoSeries.id}
          bodyPart={bodyPart}
          productInfo={productInfo?.productInfo}
          productInfoStatus={productInfo?.productInfoStatus}
        />
      )}
    </>
  );
};

DamageRow.propTypes = {
  bodyPart: PropTypes.number.isRequired,
  damages: PropTypes.arrayOf(PropTypes.shape({
    bodyPart: PropTypes.number,
    damageType: PropTypes.number,
    customDamageTypeName: PropTypes.string,
    repairDecision: PropTypes.number,
    damageSize: PropTypes.number
  })).isRequired,
  photoSeries: PropTypes.object.isRequired,
  experimentalFeatureType: PropTypes.object.isRequired,
  showExperimentalFeature: PropTypes.func.isRequired
};

const Damages = ({ photoSeries, damages }) => {
  const damagesGrouped = _.groupBy(damages, ({ bodyPart }) => bodyPart);
  const { EXPERIMENTAL_FEATURE_TYPE, showExperimentalFeature } = useLoadExperimentalFeatures();
  const { hasPermission } = authenticationService;
  const hasWindshieldRepairEstimation = photoSeries.repairEstimations?.some((re) => re.bodyPart === damagesConstants.BODY_PART.WINDSHIELD);

  return (
    <div className="damages-table__content">
      <div className="damages-table__header">
        <span>Part</span>
        <span>Repair decision</span>
        { showExperimentalFeature(EXPERIMENTAL_FEATURE_TYPE.SHOW_WINDSHIELD_REPAIR_CONFIDENCE) && <span>Confidence</span> }
        { hasPermission(VIEW_PRODUCT_INFO) && <span>Product ID</span> }
      </div>
      {
        hasWindshieldRepairEstimation && damages.length === 0 && (
          <DamageRow
            bodyPart={damagesConstants.BODY_PART.WINDSHIELD}
            damages={[]}
            photoSeries={photoSeries}
            experimentalFeatureType={EXPERIMENTAL_FEATURE_TYPE}
            showExperimentalFeature={showExperimentalFeature}
          />
        )
      }
      {
        damages.length > 0 && (
          Object.keys(damagesGrouped)?.map((key) => <DamageRow
            key={key}
            bodyPart={parseInt(key, 0)}
            damages={damagesGrouped[key]}
            photoSeries={photoSeries}
            experimentalFeatureType={EXPERIMENTAL_FEATURE_TYPE}
            showExperimentalFeature={showExperimentalFeature}
          />)
        )
      }
    </div>
  );
};

Damages.propTypes = {
  damages: PropTypes.arrayOf(PropTypes.shape({
    bodyPart: PropTypes.number,
    damageType: PropTypes.number
  })).isRequired,
  photoSeries: PropTypes.object.isRequired
};

const PhotoSeriesDamagesSkeleton = () => (
  <div className="photo-series-damages-container photo-series-card">
    <div className="photo-series-damages-container__row1">
      <div className="photo-series-damages-container__row1-col1">
        <span className="key">Vehicle damages</span>
      </div>
    </div>
    <div className="two-col-skeleton-ui-wrapper">
      <div className="pt-3">
        <SkeletonUI type="radius-horizontal" height="1rem" />
        <SkeletonUI type="radius-horizontal" height="1rem" />
        <SkeletonUI type="radius-horizontal" height="1rem" />
        <SkeletonUI type="radius-horizontal" height="1rem" />
        <SkeletonUI type="radius-horizontal" height="1rem" />
      </div>
      <div className="pt-3">
        <SkeletonUI type="radius-horizontal" height="20.438rem" />
      </div>
    </div>
  </div>
);

const PhotoSeriesDamages = ({ isLoadingPhotoSeries, damages, photoSeries, vehicleType, customer }) => {
  const [schema, setSchema] = useState(null);
  const isCar = vehicleType === workflowsConstants.VEHICLE_TYPES.CAR;

  useEffect(() => {
    if (isCar && !isLoadingPhotoSeries) {
      makeGetFile(`photoseries/${photoSeries.id}/schema`)
        .subscribe(async ({ response }) => {
          setSchema(await response.text());
        });
    }
  }, [photoSeries, isCar, isLoadingPhotoSeries, photoSeries.damages]);

  if (isLoadingPhotoSeries) return <PhotoSeriesDamagesSkeleton />;

  const windshieldRepairEstimation = photoSeries.repairEstimations?.find((re) => re.bodyPart === damagesConstants.BODY_PART.WINDSHIELD);
  const showNeedReviewInformation = windshieldRepairEstimation?.reviewTriggers?.length > 0;
  const isPhotoSeriesShared = photoSeriesHelpers.isPhotoSeriesShared(photoSeries, customer) || !photoSeries?.accessUrl;

  return (
    <div className="photo-series-damages-container photo-series-card">
      <div className="photo-series-damages-container__row1">
        <div className="photo-series-damages-container__row1-col1">
          <span className="key">Vehicle damages</span>
          <span className="value">
            <VehicleDamagedIndicator damages={damages} />
          </span>
        </div>
      </div>
      <div className="photo-series-damages-container__row2">
        <div className="damages-table">
          <Damages damages={damages} vehicleType={vehicleType} photoSeries={photoSeries} />
          {
            showNeedReviewInformation && (
              <div className="photo-series-damages-container__windshield-review">
                {
                  isPhotoSeriesShared && (
                    <Alert variant="info">
                      <div className="windshield-review-alert__title">
                        This photo series needed review due to following reasons (
                        {windshieldRepairEstimation.reviewTriggers?.length}/
                        {Object.keys(constants.REPAIRABILITY_REVIEW_TRIGGER_TYPE).length}):
                      </div>
                      <ol className="windshield-review-alert__list">
                        {windshieldRepairEstimation.reviewTriggers?.map((trigger, index) => (
                          <li key={index}>{index + 1}. {constants.REPAIRABILITY_REVIEW_TRIGGER_DESCRIPTION[trigger]}</li>
                        ))}
                      </ol>
                    </Alert>
                  )
                }
                <div className="photo-series-damages-container__reviewed-by">
                  Reviewed by:{' '}
                  <span
                    className="photo-series-damages-container__reviewed-by-name tooltip-on-hover"
                    data-tooltip={windshieldRepairEstimation.repairDecisionChangedAt
                      ? formatDateTime(windshieldRepairEstimation.repairDecisionChangedAt)
                      : ''}
                  >
                    {windshieldRepairEstimation.repairDecisionChangedBy || '-'}
                  </span>
                </div>
              </div>
            )
          }
        </div>
        {
          isCar && (
            <div style={{ width: '60%' }}>
              {
                schema !== null
                  ? <div className="sliced-car" dangerouslySetInnerHTML={{ __html: schema }} />
                  : <div><SkeletonUI type="radius-horizontal" height="25rem" /></div>
              }
            </div>
          )
        }
      </div>
    </div>
  );
};

PhotoSeriesDamages.propTypes = {
  isLoadingPhotoSeries: PropTypes.bool.isRequired,
  damages: PropTypes.arrayOf(PropTypes.shape({
    bodyPart: PropTypes.number,
    damageType: PropTypes.number,
    customDamageTypeName: PropTypes.string
  })).isRequired,
  photoSeries: PropTypes.object,
  vehicleType: PropTypes.number.isRequired,
  customer: PropTypes.object
};

export default PhotoSeriesDamages;
